import { apiClient } from '@/auth';

export type PatientDocumentRole = 'clinician' | 'patient' | 'internal';

export type PatientDocument = {
  id: number;
  patient_id: number;
  hospital_id: number;
  uploaded_by_user_id: number;
  uploaded_by_role: PatientDocumentRole;
  filename: string;
  content_type: string;
  size_bytes: number;
  visible_to_patient: boolean;
  title?: string | null;
  note?: string | null;
  created_time: string;
  updated_time: string;
};

export type UploadPatientDocumentInput = {
  file: File;
  hospitalId: number;
  title?: string;
  note?: string;
  visibleToPatient?: boolean;
  onUploadProgress?: (percent: number) => void;
};

export type PatchPatientDocumentInput = {
  visible_to_patient?: boolean;
  title?: string | null;
  note?: string | null;
};

export const patientDocumentsApi = {
  async list(patientId: number): Promise<PatientDocument[]> {
    const res = await apiClient.get<PatientDocument[]>(`/patients/${patientId}/documents`);
    return Array.isArray(res.data) ? res.data : [];
  },

  async upload(
    patientId: number,
    input: UploadPatientDocumentInput,
  ): Promise<PatientDocument> {
    const body = new FormData();
    body.append('file', input.file);
    body.append('hospital_id', String(input.hospitalId));
    if (input.title) body.append('title', input.title);
    if (input.note) body.append('note', input.note);
    if (input.visibleToPatient === true) {
      body.append('visible_to_patient', 'true');
    } else if (input.visibleToPatient === false) {
      body.append('visible_to_patient', 'false');
    }

    const res = await apiClient.post<PatientDocument>(
      `/patients/${patientId}/documents`,
      body,
      {
        timeout: 120_000,
        onUploadProgress: (event) => {
          if (!input.onUploadProgress) return;
          if (!event.total) {
            input.onUploadProgress(0);
            return;
          }
          input.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      },
    );
    return res.data;
  },

  async patch(
    patientId: number,
    documentId: number,
    body: PatchPatientDocumentInput,
  ): Promise<PatientDocument> {
    const res = await apiClient.patch<PatientDocument>(
      `/patients/${patientId}/documents/${documentId}`,
      body,
    );
    return res.data;
  },

  async remove(patientId: number, documentId: number): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/documents/${documentId}`);
  },

  /**
   * Email the patient that this document is waiting for them [FOOD-1593].
   *
   * Separate from sharing on purpose: flipping `visible_to_patient` changes
   * what the portal shows and sends nothing. The server refuses to notify
   * about a document that is not shared, and the mail deliberately carries no
   * filename, title or note.
   */
  async notify(patientId: number, documentId: number): Promise<void> {
    await apiClient.post(
      `/patients/${patientId}/documents/${documentId}/notify`,
    );
  },

  async contentBlob(patientId: number, documentId: number): Promise<Blob> {
    const res = await apiClient.get<Blob>(
      `/patients/${patientId}/documents/${documentId}/content`,
      { responseType: 'blob' },
    );
    return res.data;
  },
};
